import { useRef, useEffect, useCallback, useMemo } from 'react';

import { createPortal } from 'react-dom';

import PropTypes from 'prop-types';

import Panel from '#globalComponents/Overlay/Panel';
import { useAnchoredPosition } from '#globalHooks/useAnchoredPosition';
import { useOutsideClick } from '#globalHooks/useOutsideClick';
import { LAYER } from '#lib/constants/layers';

const WIDTHS = {
	s: 'w-64',
	m: 'w-96',
	l: 'w-[30rem]',
};

/**
 * Panel anchored to a trigger. Same surface as Modal, positioned instead of centered.
 *
 * The caller owns the open state, because a dropdown's trigger is usually an existing button whose
 * look and disabled state belong to the feature, not to this component.
 *
 * Pass `trigger` to have the popover render and measure the trigger itself. When the trigger lives
 * in a parent component, pass `anchorRef` instead and render the trigger there.
 *
 * @param {'start'|'end'|'center'|'stretch'} align - 'stretch' matches the trigger's width (selects)
 */
export default function Popover({ open, onClose, trigger, anchorRef, children, size = 'm', align = 'start' }) {
	const ownTriggerRef = useRef(null);
	const panelRef = useRef(null);

	// Callers may pass a fresh { current } object on every render, so the anchor is read through a
	// stable ref. Depending on the passed object's identity would resubscribe the listeners below
	// on every render.
	const anchorHolder = useRef(null);
	anchorHolder.current = anchorRef?.current ?? null;

	const triggerRef = anchorRef ? anchorHolder : ownTriggerRef;

	const style = useAnchoredPosition(triggerRef, panelRef, open, align);

	const refs = useMemo(() => [triggerRef, panelRef], [triggerRef]);
	const handleOutside = useCallback(() => onClose?.(), [onClose]);
	useOutsideClick(refs, handleOutside, open);

	useEffect(() => {
		if (!open) return undefined;
		const handleKeyDown = (e) => {
			if (e.key === 'Escape') onClose?.();
		};
		document.addEventListener('keydown', handleKeyDown);
		return () => document.removeEventListener('keydown', handleKeyDown);
	}, [open, onClose]);

	return (
		<>
			{trigger && (
				<span ref={ownTriggerRef} className="inline-flex">
					{trigger}
				</span>
			)}
			{open &&
				createPortal(
					<Panel
						ref={panelRef}
						role="dialog"
						style={style ?? { visibility: 'hidden' }}
						className={`fixed ${LAYER.popover} ${align === 'stretch' ? '' : WIDTHS[size]} animate-fadeIn`}
					>
						{children}
					</Panel>,
					document.body,
				)}
		</>
	);
}

Popover.propTypes = {
	open: PropTypes.bool,
	onClose: PropTypes.func,
	trigger: PropTypes.node,
	anchorRef: PropTypes.shape({ current: PropTypes.any }),
	children: PropTypes.node,
	size: PropTypes.oneOf(['s', 'm', 'l']),
	align: PropTypes.oneOf(['start', 'end', 'center', 'stretch']),
};
